import {
  Fragment, useMemo, useState, useEffect
} from 'react'
import styled from 'styled-components'
import useRouter from 'hooks/useRouter'
import useUserLoginStatus from 'hooks/useUserLoginStatus'
import useUserInfoProvider from 'hooks/useUserInfoProvider'
import useAdlProvider from 'hooks/useAdlProvider'
import useUserInfo from 'hooks/useUserInfo'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useDialogStatus from 'hooks/useDialogStatus'
import useLeverageData from 'hooks/useLeverageData'
import {
  LOGIN_PRURL,
  REGISTER_URL,
  USER_CENTER_URL,
  HELP_URL,
  FUTURES_URL,
  FUNDING_HISTORY_URL,
  DECIMAL_PLACE,
  isTestnet,
  SETTING,
  DIALOGS
} from 'constants'
import {cookie} from '@binance/js-sdk'
import {useDispatch} from 'redux-react-hook'
import {
  Logo, Card, Flex, Priceline
} from '@binance/uikit'
import useTicker from 'hooks/useTicker'
import {auth as authAction} from 'actions'
import tipMsgData from 'assets/tipMsgData'
import locales from 'assets/locales'
import {mediaJs} from 'utils/media'
import {getLabel} from 'utils/i18n'
import HeaderMenu from './HeaderMenu'

const {enableFundingHistory, leverageInfo} = SETTING

const StyledLogo = styled.div`
  img {
    width: 140px;
    height: 60px;
    margin-top: -14px;
  }
`
const StyledFuturesEntry = styled.a`
  text-decoration: none;
  color: #fff;
  font-size: 14px;
  line-height: 50px;
  margin-right: 10px;

  :hover {
    color: #f0b90b;
  }
`

export default function Header ({
  i18n, localePath = '', showPriceline = true, showFutureEntry = false
}) {
  const [showTip, setShowTip] = useState()
  const dispatch = useDispatch()
  const [loginUrl, setLoginUrl] = useState('')
  const router = useRouter()
  const {symbol, lng = 'en'} = router.query
  const ticker = useTicker(symbol)
  const {symbolInfo} = useSymbolInfo()
  const {baseAsset} = symbolInfo
  const isLogin = useUserLoginStatus()
  const [urls, setUrls] = useState({})

  useUserInfoProvider()
  useAdlProvider()

  const {userInfo, userLogout} = useUserInfo()
  const {switchDialog} = useDialogStatus()
  const {leverageData} = useLeverageData()
  const {curLeverage} = leverageData

  useEffect(() => {
    const showIt = window.localStorage.getItem('support_leverage_tip')
    const isShowTip = showIt === null ? true : JSON.parse(showIt)
    setShowTip(isShowTip)
  }, [])

  useEffect(() => {
    setLoginUrl(`${LOGIN_PRURL}?callback=${window.location.href}`.replace('$locale', lng))
    setUrls({
      login: loginUrl,
      register: REGISTER_URL.replace('$locale', lng),
      futuresEntry: `/${lng}/${FUTURES_URL}`,
      ...(!isTestnet && {usercenter: USER_CENTER_URL.replace('$locale', lng)})
    })
  }, [dispatch, lng, loginUrl])

  const logoProps = useMemo(
    () => ({
      ...(!isTestnet && {href: `/${lng}`}),
      customIcon: <img src={require('assets/logo.svg')} />
    }),
    [lng]
  )

  const infoOptions = [
    {
      name: getLabel('header-help', i18n),
      url: HELP_URL[lng] || HELP_URL.en,
      newTarget: true
    },
    {
      name: getLabel('header-fundingFeeHistory', i18n),
      url: `/${lng}/${FUNDING_HISTORY_URL}/0`,
      show: enableFundingHistory
    },
    {
      name: getLabel('header-insuranceFundHistory', i18n),
      url: `/${lng}/${FUNDING_HISTORY_URL}/1`,
      show: enableFundingHistory
    }
  ]

  const mobile = mediaJs('phone')
  const tablet = mediaJs('tablet')
  const isTablet = !mobile && tablet

  const PriceLineCom = useMemo(() => (<Priceline
    {...ticker}
    i18n={i18n}
    asset={baseAsset}
    decimalPlace={DECIMAL_PLACE}
    tipMsgData={tipMsgData}
    leverage={curLeverage}
    leverageSymbol={leverageInfo.symbol}
    onLeverageClick={() => switchDialog(DIALOGS.LEVERAGE_DIALOG)}
    showTip={showTip}
    onCloseTip={() => {
      const showIt = false
      setShowTip(showIt)
      window.localStorage.setItem('support_leverage_tip', showIt)
    }}
  />), [baseAsset, curLeverage, i18n, showTip, switchDialog, ticker])

  return useMemo(
    () => (
      <Card
        gridArea="header"
        flexDirection={isTablet ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        px={3}
        className="header-pc"
      >
        {isTablet ? (
          <Fragment>
            <Flex width="100%" justifyContent="space-between">
              <StyledLogo>
                <Logo {...logoProps} />
              </StyledLogo>
              <HeaderMenu
                username={userInfo?.email || ''}
                isLogin={isLogin}
                urls={urls}
                i18n={i18n}
                lang={lng}
                locales={locales}
                infoOptions={infoOptions}
                path={localePath || FUTURES_URL}
                handleLogout={() => {
                  userLogout().then(() => {
                    cookie.deleteCookie('logined')
                    cookie.deleteCookie('p20t')
                    cookie.deleteCookie('cr00')
                    cookie.deleteCookie('cid')
                    dispatch(authAction(false))
                  })
                }}
              />
            </Flex>
            {
              showPriceline && PriceLineCom
            }
            {
              showFutureEntry
                && <StyledFuturesEntry href={urls.futuresEntry}>
                  {getLabel('header-futures-entry', i18n)}
                </StyledFuturesEntry>
            }
          </Fragment>
        ) : (
          <Fragment>
            <StyledLogo>
              <Logo {...logoProps} />
            </StyledLogo>
            <Flex justifyContent={showPriceline ? 'space-between' : 'flex-end'} flex="1">
              {
                showPriceline && PriceLineCom
              }
              {
                showFutureEntry
                && <StyledFuturesEntry href={urls.futuresEntry}>
                  {getLabel('header-futures-entry', i18n)}
                </StyledFuturesEntry>
              }
              <HeaderMenu
                username={userInfo?.email || ''}
                isLogin={isLogin}
                urls={urls}
                i18n={i18n}
                lang={lng}
                locales={locales}
                infoOptions={infoOptions}
                path={localePath || FUTURES_URL}
                handleLogout={() => {
                  userLogout().then(() => {
                    cookie.deleteCookie('logined')
                    cookie.deleteCookie('p20t')
                    cookie.deleteCookie('cr00')
                    cookie.deleteCookie('cid')
                    dispatch(authAction(false))
                  })
                }}
              />
            </Flex>
          </Fragment>
        )}
      </Card>
    ),
    [PriceLineCom, dispatch, i18n, infoOptions, isLogin, isTablet, lng, localePath, logoProps, showFutureEntry, showPriceline, urls, userInfo, userLogout]
  )
}
